import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, Check, X, Clock, Shield, Eye, Truck, Calendar } from "lucide-react";

export default function VendorManagementPanel() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [acting, setActing] = useState(null);
  const [tab, setTab] = useState("pending");
  const [tierChanging, setTierChanging] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/vendors`);
      setPending(res.data.pending || []);
      setApproved(res.data.approved || []);
      setRejected(res.data.rejected || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleApprove = async (userId, notes = "") => {
    setActing(userId);
    try {
      await axios.post(`${API}/admin/vendors/${userId}/approve`, { notes });
      toast.success("Vendor approved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally { setActing(null); }
  };

  const handleReject = async (userId, reason = "") => {
    setActing(userId);
    try {
      await axios.post(`${API}/admin/vendors/${userId}/reject`, { reason });
      toast.success("Vendor rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rejection failed");
    } finally { setActing(null); }
  };

  const viewPhoto = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/vendors/${userId}/license-photo`);
      if (res.data.photo_base64) {
        setPreviewPhoto({ userId, data: res.data.photo_base64, extracted: res.data.extracted_fields });
      } else {
        toast.info("No license photo available");
      }
    } catch { toast.error("Could not load photo"); }
  };

  const handleTierChange = async (userId, newTier) => {
    setTierChanging(userId);
    try {
      await axios.post(`${API}/admin/vendor-analytics/${userId}/set-tier`, { analytics_tier: newTier });
      toast.success(`Analytics tier set to ${newTier}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tier change failed");
    } finally { setTierChanging(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const tabs = [
    { id: "pending", label: "Pending", count: pending.length, color: "text-amber-600" },
    { id: "approved", label: "Approved", count: approved.length, color: "text-emerald-600" },
    { id: "rejected", label: "Rejected", count: rejected.length, color: "text-red-500" },
  ];

  const trialDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="space-y-3" data-testid="vendor-management-panel">
      {/* Tab bar */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="vendor-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 text-[11px] font-semibold py-1.5 rounded-md transition ${
              tab === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`} data-testid={`vendor-tab-${t.id}`}>
            {t.label} <span className={t.color}>({t.count})</span>
          </button>
        ))}
      </div>

      {/* Pending tab */}
      {tab === "pending" && (
        <div className="space-y-2" data-testid="vendor-pending-list">
          {pending.length === 0 && <p className="text-[11px] text-slate-400 text-center py-4">No pending verifications</p>}
          {pending.map(v => (
            <div key={v.user_id} className="p-3 bg-white border border-amber-200 rounded-lg space-y-2" data-testid={`vendor-pending-${v.user_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-800 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {v.truck_name}</p>
                  <p className="text-[10px] text-slate-500">{v.city}, {v.state} &middot; License: {v.license_number}</p>
                  <p className="text-[10px] text-slate-400">Method: {v.method} &middot; Submitted: {new Date(v.submitted_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1">
                  {v.has_photo && (
                    <button onClick={() => viewPhoto(v.user_id)} className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition" data-testid={`view-photo-${v.user_id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => handleApprove(v.user_id)} disabled={acting === v.user_id}
                    className="p-1.5 bg-emerald-50 text-emerald-600 rounded hover:bg-emerald-100 transition" data-testid={`approve-${v.user_id}`}>
                    {acting === v.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => handleReject(v.user_id)} disabled={acting === v.user_id}
                    className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition" data-testid={`reject-${v.user_id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {v.details?.extracted && (
                <div className="bg-slate-50 rounded p-2 text-[10px] text-slate-600 border border-slate-100">
                  <p className="font-semibold mb-0.5">Turbo Extracted:</p>
                  <p>Name: <strong>{v.details.extracted.business_name || "N/A"}</strong> (match: {((v.details.name_match_score || 0) * 100).toFixed(0)}%)</p>
                  {v.details.extracted.owner_name && <p>Owner: {v.details.extracted.owner_name}</p>}
                  {v.details.extracted.license_number && <p>License #: {v.details.extracted.license_number}</p>}
                  {v.details.extracted.issue_date && <p>Issued: {v.details.extracted.issue_date}</p>}
                  {v.details.extracted.expiry_date && <p>Expires: {v.details.extracted.expiry_date}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approved tab */}
      {tab === "approved" && (
        <div className="space-y-2" data-testid="vendor-approved-list">
          {approved.length === 0 && <p className="text-[11px] text-slate-400 text-center py-4">No approved vendors</p>}
          {approved.map(v => {
            const days = trialDaysRemaining(v.trial_end);
            return (
              <div key={v.user_id} className="p-2.5 bg-white border border-emerald-200 rounded-lg flex items-center gap-3" data-testid={`vendor-approved-${v.user_id}`}>
                <Shield className="w-4 h-4 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-800 truncate">{v.truck_name}</p>
                  <p className="text-[10px] text-slate-500">{v.city}, {v.state} &middot; {v.method}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={v.analytics_tier || "poc"}
                    onChange={(e) => handleTierChange(v.user_id, e.target.value)}
                    disabled={tierChanging === v.user_id}
                    className="text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 font-medium"
                    data-testid={`tier-select-${v.user_id}`}
                  >
                    <option value="free">Free</option>
                    <option value="poc">POC</option>
                    <option value="growth">Growth</option>
                    <option value="pro">Pro</option>
                  </select>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold text-emerald-600 flex items-center gap-0.5"><Calendar className="w-3 h-3" /> {days !== null ? `${days}d left` : "—"}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Rejected tab */}
      {tab === "rejected" && (
        <div className="space-y-2" data-testid="vendor-rejected-list">
          {rejected.length === 0 && <p className="text-[11px] text-slate-400 text-center py-4">No rejected vendors</p>}
          {rejected.map(v => (
            <div key={v.user_id} className="p-2.5 bg-white border border-red-200 rounded-lg" data-testid={`vendor-rejected-${v.user_id}`}>
              <p className="text-xs font-bold text-slate-800">{v.truck_name}</p>
              <p className="text-[10px] text-slate-500">{v.city}, {v.state} &middot; {v.method}</p>
              <p className="text-[10px] text-red-500 mt-1">{v.reason || "No reason provided"}</p>
            </div>
          ))}
        </div>
      )}

      {/* Photo preview modal */}
      {previewPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewPhoto(null)} data-testid="photo-modal">
          <div className="bg-white rounded-xl p-4 max-w-lg w-full mx-4 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-800">License Photo</p>
              <button onClick={() => setPreviewPhoto(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <img src={`data:image/jpeg;base64,${previewPhoto.data}`} alt="License" className="w-full rounded-lg border" />
            {previewPhoto.extracted && (
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
                <p className="font-semibold">Turbo Extracted Fields:</p>
                {Object.entries(previewPhoto.extracted).map(([k, v]) => (
                  <p key={k}><span className="text-slate-400">{k}:</span> <strong>{String(v)}</strong></p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
